import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { consumeWebLoginTicket } from "../utils/api";
import { useAuth } from "../context/AuthContext";

const readTicketFromHash = () => {
    const hash = window.location.hash || "";
    const match = hash.match(/ticket=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : "";
};

const readPhoneFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search || "");
    const hashParams = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));

    return searchParams.get("phone") || hashParams.get("phone") || "";
};

const WebLogin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { token, setAuthToken } = useAuth();
    const [status, setStatus] = useState("loading");
    const [errorMessage, setErrorMessage] = useState("");
    const handledTicketRef = useRef(false);

    useEffect(() => {
        if (handledTicketRef.current) {
            return;
        }

        const ticket = readTicketFromHash();

        if (!ticket) {
            if (token) {
                navigate("/", { replace: true });
                return;
            }

            setStatus("failed");
            setErrorMessage(t("missingWebLoginTicket"));
            return;
        }

        let isMounted = true;
        handledTicketRef.current = true;

        const consumeTicket = async () => {
            try {
                const response = await consumeWebLoginTicket(ticket);

                if (!isMounted) {
                    return;
                }

                if (response?.success && response?.data) {
                    const phoneNumber = response.phone_number || response.phoneNumber || response.contact_no || response.contactNo || readPhoneFromUrl();
                    setAuthToken(response.data, phoneNumber, response.language);
                    window.history.replaceState({}, document.title, window.location.pathname);
                    const targetPath = Number(response?.web_password_set) === 0 ? "/set-password" : "/";
                    window.location.replace(targetPath);
                    return;
                }

                setStatus("failed");
                setErrorMessage(response?.data || t("unableToSignIn"));
            } catch (error) {
                if (!isMounted) {
                    return;
                }
                setStatus("failed");
                setErrorMessage(t("unableToSignIn"));
            }
        };

        consumeTicket();

        return () => {
            isMounted = false;
        };
    }, [navigate, setAuthToken, t, token]);

    return (
        <section className="flex min-h-screen items-center justify-center bg-[#031523] px-4 text-white">
            {status === "loading" ? (
                <div className="text-center">
                    <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white" />
                    <p className="text-sm text-white/70">{t("signingYouIn")}</p>
                </div>
            ) : (
                <div className="max-w-md rounded-2xl bg-white/5 p-6 text-center ring-1 ring-white/10">
                    <p className="text-base font-semibold">{errorMessage}</p>
                    <button
                        type="button"
                        onClick={() => navigate("/login", { replace: true })}
                        className="mt-4 rounded-full bg-[linear-gradient(180deg,_#A0F8FF_0%,_#00EDFF_38%,_#00B8EE_100%)] px-5 py-2 text-sm font-black text-slate-950"
                    >
                        {t("goToLogin")}
                    </button>
                </div>
            )}
        </section>
    );
};

export default WebLogin;